import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyOutlined } from '@ant-design/icons';
import { Alert, Button, Card, DatePicker, Form, InputNumber, Tag } from 'antd';
import dayjs from 'dayjs';

// Monthly AB Soft parameters (IA31) for the month being calculated.
//
// These are the only numbers that change from month to month; everything else on
// the settings page is set once per company. They used to live only in the JSON
// editor inside a collapsed panel, which is not a form a payroll clerk can use —
// and the contribution caps are not optional: with no row for the month the
// engine silently falls back to the statutory values in the region rules, which
// lag behind the current year.
//
// The JSON editor stays where it is, as the history view for earlier months.

// Fields in display order. `wide` fields lead the first row — those are the ones
// that actually move from month to month.
const FIELDS = [
  { key: 'bodIznos', tKey: 'ozMonthParamsPointValue', step: 0.0001, lead: true },
  { key: 'sati', tKey: 'ozMonthParamsHours', step: 1, lead: true },
  { key: 'minOsnovica', tKey: 'ozMonthParamsMinBase', step: 1, lead: true },
  { key: 'maxOsnovica', tKey: 'ozMonthParamsMaxBase', step: 1, lead: true },
  { key: 'minNetoZarada', tKey: 'ozMonthParamsMinNet', step: 1, lead: true },
  { key: 'maxNetoOsnovica', tKey: 'ozMonthParamsMaxNetBase', step: 1 },
  { key: 'minBrutoZarada', tKey: 'ozMonthParamsMinGross', step: 1 },
  { key: 'bodSati', tKey: 'ozMonthParamsPointHours', step: 1 },
  { key: 'prosZar', tKey: 'ozMonthParamsAvgSalary', step: 1 },
];

const toPlain = (ia31) => {
  if (!ia31) return {};
  if (ia31 instanceof Map) return Object.fromEntries(ia31);
  return { ...ia31 };
};

// Accept both decimal separators: the field is typed by hand and a comma used to
// land as 1173838 instead of 117.3838.
const parseDecimal = (v) => String(v ?? '').replace(',', '.');

export default function MonthParamsCard({
  ia31,
  onChange,
  pointValueSource,
  onPointValueChange,
}) {
  const { t } = useTranslation(['forms']);
  const rows = useMemo(() => toPlain(ia31), [ia31]);
  const months = useMemo(() => Object.keys(rows).sort(), [rows]);

  // Default to the month after the last one on file — that is the month about to
  // be calculated — but never earlier than what is already there.
  const [godMes, setGodMes] = useState(() => {
    const last = months[months.length - 1];
    if (!last) return dayjs().format('YYYYMM');
    const next = dayjs(`${last}01`, 'YYYYMMDD').add(1, 'month');
    return next.format('YYYYMM');
  });

  const current = rows[godMes] || null;
  const prevKey = useMemo(
    () => months.filter((m) => m < godMes).pop() || null,
    [months, godMes]
  );

  const patch = (key, value) => {
    const next = {
      ...rows,
      [godMes]: { ...(rows[godMes] || {}), [key]: value },
    };
    onChange(next);
    // The engine reads the point value from companyConfig.pointValue.amount, not
    // from this map, whenever the company runs without AB correction. Keep the
    // two in step so the number lives in one place for the user.
    if (key === 'bodIznos' && pointValueSource === 'NBS_RATE') {
      onPointValueChange?.(value);
    }
  };

  const copyPrevious = () => {
    if (!prevKey) return;
    const src = rows[prevKey] || {};
    const next = { ...rows, [godMes]: { ...src } };
    onChange(next);
    if (pointValueSource === 'NBS_RATE' && src.bodIznos != null) {
      onPointValueChange?.(src.bodIznos);
    }
  };

  const monthLabel = dayjs(`${godMes}01`, 'YYYYMMDD').format('MMMM YYYY');

  return (
    <Card
      size="small"
      className="mb-4 border-2 border-amber-400"
      title={
        <span className="font-semibold">
          {t('ozMonthParamsTitle')}{' '}
          <Tag color={current ? 'green' : 'red'}>{godMes}</Tag>
        </span>
      }
      extra={
        <div className="flex items-center gap-2">
          <DatePicker
            picker="month"
            size="small"
            allowClear={false}
            value={dayjs(`${godMes}01`, 'YYYYMMDD')}
            onChange={(d) => d && setGodMes(d.format('YYYYMM'))}
            format="MMMM YYYY"
          />
          <Button
            size="small"
            icon={<CopyOutlined />}
            disabled={!prevKey}
            onClick={copyPrevious}
          >
            {t('ozMonthParamsCopyPrev')}
            {prevKey ? ` (${prevKey})` : ''}
          </Button>
        </div>
      }
    >
      <div className="mb-2 text-xs text-gray-500">
        {t('ozMonthParamsHint')}
      </div>

      {!current && (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message={t('ozMonthParamsMissing', { month: monthLabel })}
          description={t('ozMonthParamsMissingHint')}
        />
      )}

      <Form layout="vertical" size="small">
        <div className="grid grid-cols-5 gap-x-4">
          {FIELDS.filter((f) => f.lead).map((f) => (
            <Form.Item key={f.key} label={t(f.tKey)} className="mb-2">
              <InputNumber
                className="w-full"
                value={current?.[f.key] ?? null}
                onChange={(v) => patch(f.key, v)}
                step={f.step}
                min={0}
                parser={parseDecimal}
                decimalSeparator=","
              />
            </Form.Item>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-x-4">
          {FIELDS.filter((f) => !f.lead).map((f) => (
            <Form.Item key={f.key} label={t(f.tKey)} className="mb-0">
              <InputNumber
                className="w-full"
                value={current?.[f.key] ?? null}
                onChange={(v) => patch(f.key, v)}
                step={f.step}
                min={0}
                parser={parseDecimal}
                decimalSeparator=","
              />
            </Form.Item>
          ))}
        </div>
      </Form>
    </Card>
  );
}
